import React, { useEffect, useMemo, useState } from "react";
import sortBy from "lodash/sortBy";
import * as Yup from "yup";
import { Formik } from "formik";
import { useQuery } from "@apollo/client/react";
import { useLocation, useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import Typography from "@mui/material/Typography";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import Input from "../../form/Input";
import Select from "../../form/Select";
import useAuth from "../../../hooks/useAuth";
import FullscreenModal from "../../modals/fullscreen-modal";
import { onKeyDown } from "./ClientAndProjectModal";
import {
  RECEIVING_WORKFLOW_CARD_OPTIONS,
  RECEIVING_WORKFLOW_ONSITE_CAPTURE,
  RECEIVING_WORKFLOW_OPTIONS,
  RECEIVING_WORKFLOW_ONSITE_MANIFEST,
  receivingWorkflowToMutationVariables,
  workflowRequiresMovewareStorageAccount,
} from "./projectReceivingWorkflow";
import ReceivingWorkflowSelectionModal from "./ReceivingWorkflowSelectionModal";
import MovewareStorageAccountField from "./MovewareStorageAccountField";
import { CLIENTS_AND_PROJECTS_QUERY } from "../../../constants/graphql/queries";
import useAvailableProjectTypesOptions from "../../../views/settings/hooks/useAvailableProjectTypesOptions";
import useQueryParams from "../../../hooks/useQueryParams";

function ReceivingWorkflowField({ value, onChange, error, cardOptions }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useQueryParams();
  const [isModalOpen, setIsModalOpen] = useState(queryParams.has("selectModeModal"));

  useEffect(() => {
    setIsModalOpen(queryParams.has("selectModeModal"));
  }, [location.search]);

  const selectedLabel =
    RECEIVING_WORKFLOW_OPTIONS.find(([optionValue]) => optionValue === value)?.[1] || "Warehouse Storage";

  const updateQueryParam = (isOpen) => {
    const params = new URLSearchParams(location.search);

    if (isOpen) {
      params.set("selectModeModal", "true");
    } else {
      params.delete("selectModeModal");
    }

    navigate({ search: params.toString() }, { replace: true });
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    updateQueryParam(false);
  };

  const handleModalSelect = (selectedValue) => {
    onChange(selectedValue);
  };

  const handleOpenModal = (event) => {
    event.preventDefault();
    setIsModalOpen(true);
    updateQueryParam(true);
  };

  return (
    <>
      <Button
        fullWidth
        variant="outlined"
        onClick={handleOpenModal}
        sx={{
          height: "40px",
          justifyContent: "space-between",
          textTransform: "none",
          fontWeight: 400,
          fontSize: "0.9375rem",
          color: value ? "text.primary" : "text.secondary",
          borderColor: error ? "error.main" : "rgba(0,0,0,0.23)",
          "&:hover": {
            borderColor: error ? "error.main" : "text.primary",
          },
          position: "relative",
          px: 1.75,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            position: "absolute",
            top: -8,
            left: 8,
            backgroundColor: "white",
            px: 0.5,
            fontSize: "0.75rem",
            color: error ? "error.main" : "text.secondary",
            lineHeight: 1,
          }}
        >
          Mode
        </Typography>
        <Box component="span">{selectedLabel}</Box>
        <KeyboardArrowDownIcon sx={{ color: error ? "error.main" : "action.active" }} />
      </Button>
      {error && (
        <FormHelperText error sx={{ mt: 1 }}>
          {error}
        </FormHelperText>
      )}
      {isModalOpen && (
        <ReceivingWorkflowSelectionModal
          open
          currentValue={value}
          onClose={handleModalClose}
          onSelect={handleModalSelect}
          cardOptions={cardOptions}
        />
      )}
    </>
   );
}

export default function ProjectModal({ initialValues, autoFocus = "clientId", onSubmit, onClose }) {
  const { user } = useAuth();
  const { settings } = user.company;
  const movewareEnabled = user.company.enabledIntegrationProviders?.includes("moveware");
  const [movewarePreviewVisible, setMovewarePreviewVisible] = useState(false);
  const { data: { clients = [] } = {} } = useQuery(CLIENTS_AND_PROJECTS_QUERY, {
    fetchPolicy: "network-only",
  });
  const assignableClientIds = useMemo(
    () => new Set((user.clientIds || []).map(String)),
    [user.clientIds]
  );
  const assignableClients = useMemo(
    () =>
      clients.filter(
        client => !client.disabled && assignableClientIds.has(String(client.id))
      ),
    [assignableClientIds, clients]
  );
  const projectTypeOptions = useAvailableProjectTypesOptions().filter(([value]) => value);
  const filteredCardOptions = useMemo(() =>
    RECEIVING_WORKFLOW_CARD_OPTIONS.filter((o) =>
      o.value !== RECEIVING_WORKFLOW_ONSITE_MANIFEST || settings.onsite_manifest
    ),
    [settings.onsite_manifest]
  );

  const requiresMovewareForWorkflow = workflow =>
    movewareEnabled && workflowRequiresMovewareStorageAccount(workflow);

  const validationSchema = Yup.object().shape({
    clientId: Yup.string().required(`${settings.filter_field1} required`),
    projectName: Yup.string().required(`${settings.filter_field2} Name required`),
    movewareStorageAccountId: Yup.string().when(
      ["receivingWorkflow", "skipMovewareStorageAccount"],
      {
        is: (workflow, skipped) => !skipped && requiresMovewareForWorkflow(workflow),
        then: schema =>
          schema
            .trim()
            .matches(/^\d+$/, "Moveware Storage Account ID must contain only digits")
            .required("Moveware Storage Account ID required"),
        otherwise: schema => schema,
      }
    ),
    externalRecordId: Yup.string().when(["receivingWorkflow", "skipMovewareStorageAccount"], {
      is: (workflow, skipped) => !skipped && requiresMovewareForWorkflow(workflow),
      then: schema => schema.required("Look up and confirm the Moveware storage account"),
      otherwise: schema => schema,
    }),
    ...((settings.receiving_wizard || settings.project_types) && {
      projectType: Yup.string().required(`${settings.filter_field2} Field Template is required`),
    }),
  });

  const formikOnSubmit = (values, { setErrors, setStatus, setSubmitting }) => {
    setSubmitting(true);
    const { projectType, receivingWorkflow, skipMovewareStorageAccount, ...rest } = values;
    delete rest.movewareStorageAccountId;
    const workflow =
      settings.receiving_wizard || skipMovewareStorageAccount
        ? receivingWorkflow
        : undefined;
    const workflowVars = workflow
      ? receivingWorkflowToMutationVariables(workflow)
      : {};
    if (!workflowRequiresMovewareStorageAccount(receivingWorkflow)) {
      delete rest.externalRecordId;
    }
    const variables = {
      ...rest,
      ...workflowVars,
      ...(projectType ? { projectType } : {}),
    };
    onSubmit({
      variables,
      onSuccess: () => {
        setSubmitting(false);
        setStatus({ success: true });
        onClose();
      },
      onFailure: (errors) => {
        setStatus({ success: false });
        setErrors(errors);
        setSubmitting(false);
      },
    });
  };

  return (
    <Formik
      initialValues={{
        ...initialValues,
        movewareStorageAccountId: "",
        externalRecordId: "",
        skipMovewareStorageAccount: false,
      }}
      validationSchema={validationSchema}
      onSubmit={formikOnSubmit}
    >
      {({ errors, isSubmitting, handleSubmit, setFieldValue, values }) => {
        const skippedMoveware = values.skipMovewareStorageAccount;
        const needsMovewareLookup = requiresMovewareForWorkflow(values.receivingWorkflow);
        const showProjectFields = !movewareEnabled || values.externalRecordId || skippedMoveware;
        const showNonStorageAction =
          movewareEnabled &&
          !values.externalRecordId &&
          !skippedMoveware &&
          !movewarePreviewVisible;

        const handleAddNonStorage = () => {
          setFieldValue("skipMovewareStorageAccount", true);
          setFieldValue("receivingWorkflow", RECEIVING_WORKFLOW_ONSITE_CAPTURE);
          setFieldValue("movewareStorageAccountId", "");
          setFieldValue("externalRecordId", "");
        };

        const handleWorkflowChange = workflow => {
          setFieldValue("receivingWorkflow", workflow);
          if (workflowRequiresMovewareStorageAccount(workflow)) {
            setFieldValue("skipMovewareStorageAccount", false);
          }
        };

        return (
        <form onSubmit={handleSubmit}>
          <FullscreenModal
            open
            id="project"
            title={`Create New ${settings.filter_field2}`}
            titleFontSize="1.5rem"
            maxWidth={500}
            onClose={onClose}
            disableFullScreen
            actions={
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box>
                  {showNonStorageAction && (
                    <Button
                      id="addNonStorageProject"
                      variant="text"
                      color="primary"
                      type="button"
                      onClick={handleAddNonStorage}
                    >
                      Add Non-Storage {settings.filter_field2}
                    </Button>
                  )}
                </Box>
                <Button
                  id="createNewProjectSubmit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || (needsMovewareLookup && !values.externalRecordId)}
                  type="submit"
                  onClick={handleSubmit}
                >
                  Create New {settings.filter_field2}
                </Button>
              </Box>
            }
          >
            <Grid container spacing={1} sx={{ mt: 1 }}>
              {movewareEnabled && !skippedMoveware && (
                <MovewareStorageAccountField
                  clients={assignableClients}
                  onPreviewChange={setMovewarePreviewVisible}
                />
              )}
              {showProjectFields && (
                <>
                  <Grid size={12}>
                    <Select
                      key={`client-select-${clients.length}`}
                      size="small"
                      name="clientId"
                      showNAButton={false}
                      autoFocus={autoFocus === "clientId"}
                      SelectDisplayProps={{
                        onKeyDown(event) {
                          if (event.code === "Tab") {
                            const input = document.querySelector('[name="projectName"]');
                            input.focus();
                          }
                        },
                      }}
                      label={`Assign to a ${settings.filter_field1}`}
                      options={sortBy(assignableClients, "name").map((v) => [v.id, v.name])}
                    />
                  </Grid>
                  <Grid size={12}>
                    <Input
                      name="projectName"
                      label={`New ${settings.filter_field2} Name`}
                      autoFocus={autoFocus === "projectName"}
                      autoComplete="off"
                      showNAButton={false}
                      inputProps={{ onKeyDown }}
                    />
                  </Grid>
                  {(settings.receiving_wizard || settings.project_types) && (
                    <Grid size={12}>
                      <Select
                        size="small"
                        name="projectType"
                        label={`${settings.filter_field2} Field Template`}
                        showNAButton={false}
                        options={projectTypeOptions}
                      />
                    </Grid>
                  )}
                  {settings.receiving_wizard && (
                    <Grid size={12}>
                      <ReceivingWorkflowField
                        value={values.receivingWorkflow}
                        onChange={handleWorkflowChange}
                        error={errors.receivingWorkflow}
                        cardOptions={filteredCardOptions}
                      />
                    </Grid>
                  )}
                </>
              )}
            </Grid>
            {errors.submit && (
              <Box mt={2}>
                <FormHelperText error>{errors.submit}</FormHelperText>
              </Box>
            )}
          </FullscreenModal>
        </form>
        );
      }}
    </Formik>
  );
}
